import type { CustomerPaymentTermsCode } from '@/lib/services/customers/customers.types'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { CustomerPaymentTerms } from '@/lib/enums/CustomerPaymentTerms'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import dayjs from '@/lib/dayjs'
import type { ConfigType } from 'dayjs'
import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'

import type { DuplicateSalesOrderCartResult } from './sales-orders.types'

/**
 * Index search params as routes and helpers pass them around. The URL schemas below validate the
 * known keys; callers may carry extra keys through, so the record stays open.
 */
export interface SalesOrderSearchRecord {
  [key: string]: unknown
  search?: string | undefined
  due?: string | undefined
  due_range?: string | undefined
  created?: string | undefined
  created_range?: string | undefined
  filter?: string | undefined
  source_filter?: string | undefined
  payment_filter?: string | undefined
  fulfillment_type_filter?: string | undefined
  fulfillment_status_filter?: string | undefined
  assignment_filter?: string | undefined
  layout?: string | undefined
  month?: string | undefined
  sort?: string | undefined
  page?: number | undefined
  page_size?: number | undefined
  customerId?: number | '' | undefined
}

type SearchParamsInput = SalesOrderSearchRecord | null | undefined

export type SalesOrderLayout = 'grid' | 'list' | 'calendar'

export const salesOrderSchema = z.object({ salesOrderId: idSchema('order') })
export const salesOrderPurchaserSchema = z.object({
  customer: z.object(
    { id: idSchema('customer'), name: z.string() },
    { error: 'Choose a customer.' }
  ),
})
const paymentTermDays: Record<Exclude<CustomerPaymentTermsCode, 'custom'>, number> = {
  [CustomerPaymentTerms.DueOnReceipt.Value]: 0,
  [CustomerPaymentTerms.Net15.Value]: 15,
  [CustomerPaymentTerms.Net30.Value]: 30,
  [CustomerPaymentTerms.Net60.Value]: 60,
}

export const salesOrderPaymentTermsSchema = z
  .object({
    payment_terms_code: z.enum(Object.values(CustomerPaymentTerms).map((term) => term.Value)),
    payment_terms_days: z.union([z.string(), z.number(), z.null()]).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.payment_terms_code !== CustomerPaymentTerms.Custom.Value) return

    const days = String(value.payment_terms_days ?? '').trim()
    if (!/^\d+$/.test(days) || Number(days) > 365) {
      ctx.addIssue({
        code: 'custom',
        path: ['payment_terms_days'],
        message: 'Enter a whole number of days from 0 to 365.',
      })
    }
  })
  .transform((value) => ({
    payment_terms_code: value.payment_terms_code,
    payment_terms_days:
      value.payment_terms_code === CustomerPaymentTerms.Custom.Value
        ? Number(value.payment_terms_days)
        : paymentTermDays[value.payment_terms_code],
  }))

export const duplicateSalesOrderReviewFulfillmentTypes = [
  FulfillmentType.Pickup.Value,
  FulfillmentType.Delivery.Value,
]

/** The fulfillment whose date staff review on a duplicated order: its pickup or delivery form opens. */
export type DuplicateSalesOrderReviewFulfillmentType =
  (typeof duplicateSalesOrderReviewFulfillmentTypes)[number]

function isDuplicateSalesOrderReviewFulfillmentType(
  value: string
): value is (typeof duplicateSalesOrderReviewFulfillmentTypes)[number] {
  return duplicateSalesOrderReviewFulfillmentTypes.some((type) => type === value)
}

export const salesOrderAsideViews = {
  editRecipient: 'edit-recipient',
  editFulfillmentDestination: 'edit-fulfillment-destination',
  editFulfillmentAddress: 'edit-fulfillment-address',
} as const
export const salesOrderArrangementPhotoSearchActions = {
  add: 'add',
} as const
export const salesOrderFixAddressSearch = {
  aside: salesOrderAsideViews.editFulfillmentAddress,
}
export const salesOrderDetailSearchSchema = z
  .object({
    aside: z
      .enum([
        salesOrderAsideViews.editRecipient,
        salesOrderAsideViews.editFulfillmentDestination,
        salesOrderAsideViews.editFulfillmentAddress,
      ])
      .optional(),
    arrangementPhoto: z
      .enum([salesOrderArrangementPhotoSearchActions.add])
      .optional()
      .catch(undefined),
    // An order change that took every item off sends staff here to cancel the order.
    cancel: z.boolean().optional().catch(undefined),
  })
  .loose()
export const salesOrderCreateSearchSchema = z.object({
  cartId: idSchema('cart', true),
  duplicateReview: z.enum(duplicateSalesOrderReviewFulfillmentTypes).optional().catch(undefined),
})

export const DEFAULT_SALES_ORDER_PAGE_SIZE = 20
export const DEFAULT_GRID_SALES_ORDER_FILTER = SalesOrderFulfillmentStatus.ToDo.Value
export const DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER =
  SalesOrderFulfillmentStatus.ToDo.Value
export const DEFAULT_GRID_SALES_ORDER_SORT = 'fulfillment_due_date_asc'
export const DEFAULT_CALENDAR_SALES_ORDER_SORT = 'fulfillment_due_date_asc'
export const SALES_ORDER_CALENDAR_MONTH_FORMAT = 'YYYY-MM'
export const NEEDS_FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER = 'needs_fsn_network'
export const FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER = 'fsn_network'
// Cancelled orders list only here, never under a payment status.
export const CANCELLED_SALES_ORDER_PAYMENT_FILTER = 'cancelled'
export const SALES_ORDER_SOURCE_FILTERS = {
  All: 'all',
  Web: 'web',
  Pos: 'pos',
  WebPos: 'web_pos',
  Subscriptions: 'tukios_flowers_subscription',
  Condolences: 'condolences',
  FsnNetwork: 'fsn_network',
} as const
export const salesOrderFilterDefaults = {
  source_filter: SALES_ORDER_SOURCE_FILTERS.All,
  payment_filter: 'all',
  fulfillment_type_filter: 'all',
  fulfillment_status_filter: 'all',
  assignment_filter: 'all',
}
const salesOrderAssignmentFilterSchema = z
  .union([z.enum(['all', 'assigned', 'unassigned']), z.string().regex(/^user:[1-9]\d*$/)])
  .optional()
  .catch(undefined)
interface DefaultSalesOrderSearchParams {
  layout: SalesOrderLayout
  filter: string
  source_filter: string
  payment_filter: string
  fulfillment_type_filter: string
  fulfillment_status_filter: string
  assignment_filter: string
  sort: SalesOrderSort
  page: number
  page_size: number
}

export const defaultSalesOrderSearchParams: DefaultSalesOrderSearchParams = {
  layout: 'list',
  filter: 'all',
  ...salesOrderFilterDefaults,
  sort: 'created_desc',
  page: 1,
  page_size: DEFAULT_SALES_ORDER_PAGE_SIZE,
}
export const strippedSalesOrderSearchParams = {
  layout: defaultSalesOrderSearchParams.layout,
  page: defaultSalesOrderSearchParams.page,
  page_size: defaultSalesOrderSearchParams.page_size,
}
export const salesOrderSortValues = [
  'id_asc',
  'id_desc',
  'recipient_asc',
  'recipient_desc',
  'purchaser_asc',
  'purchaser_desc',
  'organization_asc',
  'organization_desc',
  'source_asc',
  'source_desc',
  'fulfillment_status_asc',
  'fulfillment_status_desc',
  'fulfillment_due_date_asc',
  'fulfillment_due_date_desc',
  'fulfillment_type_asc',
  'fulfillment_type_desc',
  'payment_status_asc',
  'payment_status_desc',
  'created_asc',
  'created_desc',
  'item_count_asc',
  'item_count_desc',
  'arrangement_photo_count_asc',
  'arrangement_photo_count_desc',
  'total_asc',
  'total_desc',
] as const
export type SalesOrderSort = (typeof salesOrderSortValues)[number]

const legacySalesOrderSortMap: Partial<Record<string, SalesOrderSort>> = {
  purchaser_asc: 'recipient_asc',
  purchaser_desc: 'recipient_desc',
}

export function normalizeSalesOrderSort(sort: string | undefined) {
  // Property keys coerce to strings, so an undefined sort looks up 'undefined' and falls through.
  return legacySalesOrderSortMap[String(sort)] ?? sort
}

// URL parameters schema for sales orders
// This is anything that could show up in the URL on the index page
export const salesOrderUrlSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  due: z.string().optional(),
  due_range: z.string().optional(),
  created: z.string().optional(),
  created_range: z.string().optional(),
  filter: z.string().optional().catch(undefined),
  source_filter: z.string().optional().catch(undefined),
  payment_filter: z.string().optional().catch(undefined),
  fulfillment_type_filter: z.string().optional().catch(undefined),
  fulfillment_status_filter: z.string().optional().catch(undefined),
  assignment_filter: salesOrderAssignmentFilterSchema,
  layout: z.enum(['grid', 'list', 'calendar']).catch(defaultSalesOrderSearchParams.layout),
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
  sort: z.enum(salesOrderSortValues).optional().catch(undefined),
  page: z.coerce.number().int().positive().catch(defaultSalesOrderSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultSalesOrderSearchParams.page_size),
  customerId: idSchema('customer', true),
})

// This is anything that we would want to send to the backend in our query
export const salesOrderSearchParamsSchema = z
  .object({
    search: z.string().optional(),
    due: z.string().optional(),
    due_range: z.string().optional(),
    created: z.string().optional(),
    created_range: z.string().optional(),
    filter: z.string().optional().catch(undefined),
    source_filter: z.string().optional().catch(undefined),
    payment_filter: z.string().optional().catch(undefined),
    fulfillment_type_filter: z.string().optional().catch(undefined),
    fulfillment_status_filter: z.string().optional().catch(undefined),
    assignment_filter: salesOrderAssignmentFilterSchema,
    layout: z.enum(['grid', 'list', 'calendar']).catch(defaultSalesOrderSearchParams.layout),
    month: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
      .optional()
      .catch(undefined),
    sort: z.enum(salesOrderSortValues).optional().catch(undefined),
    page: z.coerce.number().int().positive().catch(defaultSalesOrderSearchParams.page),
    page_size: z.coerce
      .number()
      .int()
      .positive()
      .max(100)
      .catch(defaultSalesOrderSearchParams.page_size),
    customerId: idSchema('customer', true),
  })
  // Remove other keys from the result
  .strip()

export type SalesOrderUrlSearch = z.output<typeof salesOrderUrlSearchSchema>
export type SalesOrderSearchParams = z.output<typeof salesOrderSearchParamsSchema>

export function isSalesOrderUserAssignmentFilter(value: unknown): value is string {
  return typeof value === 'string' && /^user:[1-9]\d*$/.test(value)
}

export function getCurrentSalesOrderCalendarMonth(now: ConfigType = dayjs()): string {
  return dayjs(now).format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
}

function getSalesOrderDateRangeMonth(dateRange: string) {
  const [startDate, endDate] = String(dateRange).split('to')
  const parsedStartDate = startDate ? dayjs(startDate) : null
  const parsedEndDate = endDate ? dayjs(endDate) : null

  if (parsedStartDate?.isValid() && parsedEndDate?.isValid()) {
    const isVisibleCalendarGrid =
      parsedStartDate.day() === 0 && parsedEndDate.diff(parsedStartDate, 'day') === 41

    // Visible month grids often start in the previous month.
    return (isVisibleCalendarGrid ? parsedStartDate.add(7, 'day') : parsedStartDate).format(
      SALES_ORDER_CALENDAR_MONTH_FORMAT
    )
  }

  if (parsedStartDate?.isValid()) {
    return parsedStartDate.format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
  }

  if (parsedEndDate?.isValid()) {
    return parsedEndDate.format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
  }

  return undefined
}

type SalesOrderCalendarMonthParams = Pick<
  SalesOrderSearchRecord,
  'month' | 'due' | 'due_range' | 'created' | 'created_range'
>

export function getSalesOrderCalendarMonth(
  params: SalesOrderCalendarMonthParams | null = {},
  now: ConfigType = dayjs()
): string {
  if (params?.month) {
    return params.month
  }

  if (params?.due) {
    return dayjs(params.due).format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
  }

  if (params?.due_range) {
    const rangeMonth = getSalesOrderDateRangeMonth(params.due_range)

    if (rangeMonth) {
      return rangeMonth
    }
  }

  if (params?.created) {
    return dayjs(params.created).format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
  }

  if (params?.created_range) {
    const rangeMonth = getSalesOrderDateRangeMonth(params.created_range)

    if (rangeMonth) {
      return rangeMonth
    }
  }

  return getCurrentSalesOrderCalendarMonth(now)
}

function getSalesOrderCalendarMonthStart(month: string | undefined, now: ConfigType = dayjs()) {
  return dayjs(`${getSalesOrderCalendarMonth({ month }, now)}-01`).startOf('month')
}

export function getSalesOrderCalendarMonthLabel(
  month: string | undefined,
  now: ConfigType = dayjs()
): string {
  return getSalesOrderCalendarMonthStart(month, now).format('MMMM YYYY')
}

export function getShiftedSalesOrderCalendarMonth(
  month: string | undefined,
  offset: number,
  now: ConfigType = dayjs()
): string {
  return getSalesOrderCalendarMonthStart(month, now)
    .add(offset, 'month')
    .format(SALES_ORDER_CALENDAR_MONTH_FORMAT)
}

export function getSalesOrderCalendarVisibleDateRange(
  month: string | undefined,
  now: ConfigType = dayjs()
): string {
  const startOfMonth = getSalesOrderCalendarMonthStart(month, now)
  const startDate = startOfMonth.subtract(startOfMonth.day(), 'day')
  const endDate = startDate.add(41, 'day')

  return `${startDate.format('YYYY-MM-DD')}to${endDate.format('YYYY-MM-DD')}`
}

const fulfillmentTypeLegacyFilterMap = Object.fromEntries(
  [
    FulfillmentType.InStore,
    FulfillmentType.Pickup,
    FulfillmentType.Delivery,
    FulfillmentType.Shipping,
  ]
    .filter(Boolean)
    .map(({ Value }) => [Value, { fulfillment_type_filter: Value }])
)

const legacySalesOrderSourceFilterMap: Partial<Record<string, { source_filter: string }>> =
  Object.fromEntries(
    Object.values(SALES_ORDER_SOURCE_FILTERS)
      .filter(
        (value) =>
          value !== SALES_ORDER_SOURCE_FILTERS.All &&
          value !== SALES_ORDER_SOURCE_FILTERS.FsnNetwork
      )
      .map((value) => [value, { source_filter: value }])
  )

const legacySalesOrderNonSourceFilterMap: Partial<Record<string, SalesOrderSearchRecord>> = {
  [SalesOrderPaymentStatus.OpenBalance.Value]: {
    payment_filter: SalesOrderPaymentStatus.OpenBalance.Value,
  },
  [SalesOrderPaymentStatus.PartiallyPaid.Value]: {
    payment_filter: SalesOrderPaymentStatus.PartiallyPaid.Value,
  },
  [SalesOrderPaymentStatus.Paid.Value]: {
    payment_filter: SalesOrderPaymentStatus.Paid.Value,
  },
  ...fulfillmentTypeLegacyFilterMap,
  [SalesOrderFulfillmentStatus.ToDo.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.ToDo.Value,
  },
  [SalesOrderFulfillmentStatus.InProgress.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.InProgress.Value,
  },
  [SalesOrderFulfillmentStatus.Staged.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.Staged.Value,
  },
  [SalesOrderFulfillmentStatus.ReadyForPickup.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.ReadyForPickup.Value,
  },
  [SalesOrderFulfillmentStatus.ReadyForDelivery.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.ReadyForDelivery.Value,
  },
  [SalesOrderFulfillmentStatus.Out.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.Out.Value,
  },
  [SalesOrderFulfillmentStatus.Fulfilled.Value]: {
    fulfillment_status_filter: SalesOrderFulfillmentStatus.Fulfilled.Value,
  },
  past_due: {
    fulfillment_status_filter: 'past_due',
  },
  [NEEDS_FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER]: {
    fulfillment_status_filter: NEEDS_FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
  },
  [FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER]: {
    fulfillment_status_filter: FSN_NETWORK_SALES_ORDER_FULFILLMENT_STATUS_FILTER,
  },
  assigned: {
    assignment_filter: 'assigned',
  },
  unassigned: {
    assignment_filter: 'unassigned',
  },
}

export function applyLegacySalesOrderSourceFilterParams<TParams extends SalesOrderSearchRecord>(
  parsedParams: TParams,
  rawParams: SearchParamsInput = {}
): TParams {
  const legacyFilter = parsedParams.filter
  const mappedFilters = legacySalesOrderSourceFilterMap[String(legacyFilter)]

  if (!mappedFilters) {
    return parsedParams
  }

  return {
    ...parsedParams,
    source_filter: hasExplicitSearchParam(rawParams, 'source_filter')
      ? parsedParams.source_filter
      : mappedFilters.source_filter,
    filter: defaultSalesOrderSearchParams.filter,
  }
}

export function applyLegacySalesOrderFilterParams<TParams extends SalesOrderSearchRecord>(
  parsedParams: TParams,
  rawParams: SearchParamsInput = {}
): TParams {
  const sourceMappedParams = applyLegacySalesOrderSourceFilterParams(parsedParams, rawParams)

  if (sourceMappedParams !== parsedParams) {
    return sourceMappedParams
  }

  const legacyFilter = parsedParams.filter
  const mappedFilters = legacySalesOrderNonSourceFilterMap[String(legacyFilter)]

  if (!mappedFilters) {
    return parsedParams
  }

  return Object.entries(mappedFilters).reduce(
    (nextParams, [key, value]) => ({
      ...nextParams,
      [key]: hasExplicitSearchParam(rawParams, key) ? nextParams[key] : value,
    }),
    { ...parsedParams }
  )
}

function parseSalesOrderSearchParams(params: SearchParamsInput) {
  const parsedParams: Partial<SalesOrderSearchParams> =
    salesOrderSearchParamsSchema.safeParse(params)?.data ?? {}
  const mappedParams = applyLegacySalesOrderFilterParams(parsedParams, params)
  const layout = parsedParams.layout ?? defaultSalesOrderSearchParams.layout
  const hasLegacyFilterOverride = hasExplicitSearchParam(params, 'filter')
  const defaultSort =
    layout === 'grid'
      ? DEFAULT_GRID_SALES_ORDER_SORT
      : layout === 'calendar'
        ? DEFAULT_CALENDAR_SALES_ORDER_SORT
        : defaultSalesOrderSearchParams.sort
  const sort = normalizeSalesOrderSort(parsedParams.sort) ?? defaultSort

  return {
    ...defaultSalesOrderSearchParams,
    ...mappedParams,
    layout,
    filter: mappedParams.filter ?? parsedParams.filter ?? defaultSalesOrderSearchParams.filter,
    fulfillment_status_filter:
      mappedParams.fulfillment_status_filter ??
      (layout === 'grid' && !hasLegacyFilterOverride
        ? DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER
        : salesOrderFilterDefaults.fulfillment_status_filter),
    sort,
  }
}

export function hasExplicitSearchParam(params: SearchParamsInput, key: string) {
  return (
    params &&
    Object.prototype.hasOwnProperty.call(params, key) &&
    params[key] !== undefined &&
    params[key] !== null
  )
}

export function getImplicitSalesOrderFilterKeys(params: SearchParamsInput): string[] {
  const parsedParams = parseSalesOrderSearchParams(params)
  const implicitFilterKeys: string[] = []

  if (parsedParams.layout === 'grid') {
    if (
      !hasExplicitSearchParam(params, 'filter') &&
      !hasExplicitSearchParam(params, 'fulfillment_status_filter')
    ) {
      implicitFilterKeys.push('fulfillment_status_filter')
    }

    if (!hasExplicitSearchParam(params, 'sort')) {
      implicitFilterKeys.push('sort')
    }
  }

  if (parsedParams.layout === 'calendar') {
    implicitFilterKeys.push('due_range')
  }

  return implicitFilterKeys
}

export function stripSalesOrderSearchParams({
  search,
  next,
}: {
  search: SalesOrderSearchRecord
  next: (search: SalesOrderSearchRecord) => SalesOrderSearchRecord
}): SalesOrderSearchRecord {
  const result = { ...next(search) }
  const layout = result.layout ?? defaultSalesOrderSearchParams.layout
  const defaultFulfillmentStatusFilter =
    layout === 'grid'
      ? DEFAULT_GRID_SALES_ORDER_FULFILLMENT_STATUS_FILTER
      : salesOrderFilterDefaults.fulfillment_status_filter
  const defaultSort =
    layout === 'grid'
      ? DEFAULT_GRID_SALES_ORDER_SORT
      : layout === 'calendar'
        ? DEFAULT_CALENDAR_SALES_ORDER_SORT
        : defaultSalesOrderSearchParams.sort

  Object.entries(strippedSalesOrderSearchParams).forEach(([key, value]) => {
    if (result[key] === value) {
      delete result[key]
    }
  })

  if (result.sort === defaultSort) {
    delete result.sort
  }

  if (
    layout === 'grid' &&
    result.filter === defaultSalesOrderSearchParams.filter &&
    hasExplicitSearchParam(search, 'filter') &&
    !hasExplicitSearchParam(search, 'fulfillment_status_filter')
  ) {
    result.fulfillment_status_filter = salesOrderFilterDefaults.fulfillment_status_filter
  }

  if (result.filter === defaultSalesOrderSearchParams.filter) {
    delete result.filter
  }

  Object.entries(salesOrderFilterDefaults).forEach(([key, value]) => {
    const defaultValue =
      key === 'fulfillment_status_filter' ? defaultFulfillmentStatusFilter : value

    if (result[key] === defaultValue) {
      delete result[key]
    }
  })

  if (layout === 'calendar') {
    const calendarMonth = getSalesOrderCalendarMonth(result)

    delete result.due
    delete result.due_range
    delete result.created
    delete result.created_range

    result.month = result.month ?? calendarMonth
  }

  if (layout !== 'calendar') {
    delete result.month
  }

  if (layout === 'calendar' && result.month === getCurrentSalesOrderCalendarMonth()) {
    delete result.month
  }

  return result
}

// Get just the search params to send to the backend
export const getSalesOrderSearchParams = (params: SearchParamsInput) => {
  const parsedParams = parseSalesOrderSearchParams(params)
  const trimmedSearch = parsedParams.search?.trim()
  const baseFilters = {
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
    ...(parsedParams.due ? { due: parsedParams.due } : {}),
    ...(parsedParams.due_range ? { due_range: parsedParams.due_range } : {}),
    ...(parsedParams.created ? { created: parsedParams.created } : {}),
    ...(parsedParams.created_range ? { created_range: parsedParams.created_range } : {}),
    filter: parsedParams.filter,
    source_filter: parsedParams.source_filter,
    payment_filter: parsedParams.payment_filter,
    fulfillment_type_filter: parsedParams.fulfillment_type_filter,
    fulfillment_status_filter: parsedParams.fulfillment_status_filter,
    assignment_filter: parsedParams.assignment_filter,
    ...(parsedParams.customerId ? { customerId: parsedParams.customerId } : {}),
  }

  if (parsedParams.layout === 'grid') {
    return {
      ...baseFilters,
      sort: parsedParams.sort,
      page: parsedParams.page,
      page_size: parsedParams.page_size,
    }
  }

  if (parsedParams.layout === 'calendar') {
    const calendarFilters = {
      ...baseFilters,
      sort: parsedParams.sort,
    }

    delete calendarFilters.due
    delete calendarFilters.due_range
    delete calendarFilters.created
    delete calendarFilters.created_range

    return {
      ...calendarFilters,
      due_range: getSalesOrderCalendarVisibleDateRange(getSalesOrderCalendarMonth(parsedParams)),
    }
  }

  return {
    ...baseFilters,
    sort: parsedParams.sort,
    page: parsedParams.page,
    page_size: parsedParams.page_size,
  }
}

export function getDuplicateSalesOrderCartSearchParams(
  response: DuplicateSalesOrderCartResult | null | undefined,
  fallbackFulfillmentType: string | null | undefined = null
) {
  const cartId = response?.cart_id ?? response?.id
  const reviewFulfillmentType =
    response?.requires_fulfillment_review === false
      ? null
      : (response?.duplicate_review_fulfillment_type ??
        response?.fulfillment_type ??
        fallbackFulfillmentType)

  return {
    cartId,
    ...(typeof reviewFulfillmentType === 'string' &&
    isDuplicateSalesOrderReviewFulfillmentType(reviewFulfillmentType)
      ? { duplicateReview: reviewFulfillmentType }
      : {}),
  }
}
